import { PDFDocument, type PDFFont, type PDFImage, type PDFPage, rgb } from "pdf-lib";
import { DISCLAIMER_SKB } from "@/data/holidays";

// Brand-aligned PDF palette (matches the cream/navy/gold web theme).
export const RED = rgb(0.74, 0.22, 0.22);    // libur nasional / rose
export const BLUE = rgb(0.18, 0.45, 0.55);   // cuti bersama / teal
export const GREEN = rgb(0.20, 0.50, 0.32);  // hari besar Islam
export const AMBER = rgb(0.83, 0.63, 0.16);  // gold accent
export const GRAY = rgb(0.40, 0.42, 0.48);
export const TEXT = rgb(0.10, 0.13, 0.22);   // navy ink
export const LIGHT = rgb(0.93, 0.91, 0.86);  // cream divider

export interface BaseOpts {
  logo?: File | null;
  useDefaultLogo?: boolean;
  siteUrl?: string;
}

export async function embedLogo(
  doc: PDFDocument,
  logoFile?: File | null,
  useDefault = false,
): Promise<PDFImage | null> {
  if (logoFile) {
    const buf = await logoFile.arrayBuffer();
    try {
      if (logoFile.type.includes("png")) return await doc.embedPng(buf);
      return await doc.embedJpg(buf);
    } catch {
      return null;
    }
  }
  if (useDefault && typeof fetch !== "undefined") {
    try {
      const res = await fetch("/logo-pdf.png");
      if (!res.ok) return null;
      const buf = await res.arrayBuffer();
      return await doc.embedPng(buf);
    } catch {
      return null;
    }
  }
  return null;
}

/** Auto-fit logo into a max box. Returns drawn width so caller can offset header text. */
export function drawHeaderLogo(
  page: PDFPage,
  logo: PDFImage | null,
  x: number,
  y: number,
  maxH = 44,
  maxW = 160,
): number {
  if (!logo) return 0;
  const ratio = logo.width / logo.height;
  let h = maxH;
  let w = h * ratio;
  if (w > maxW) {
    w = maxW;
    h = w / ratio;
  }
  page.drawImage(logo, { x, y: y - h, width: w, height: h });
  return w;
}

/**
 * Draw the brand header (logo mark + "hari.co.id" wordmark + tagline) anchored
 * at the top-left of a page. Returns the bottom Y coordinate so callers can
 * place the page title on the line below without overlap.
 *
 * Layout (anchor at topY, leftX = x):
 *   ┌──────┐  hari.co.id          (13pt bold)
 *   │ logo │  <tagline>           (8pt gray)
 *   └──────┘
 * Returns: bottom Y of the brand block (pt). Title should be drawn ABOVE
 * (smaller y) this value with a small gap.
 */
export function drawBrandHeader(
  page: PDFPage,
  logo: PDFImage | null,
  font: PDFFont,
  bold: PDFFont,
  x: number,
  topY: number,
  tagline = "Kalender Indonesia",
  markSize = 32,
): number {
  let textX = x;
  if (logo) {
    // square mark, fixed size — never overlaps the wordmark
    page.drawImage(logo, { x, y: topY - markSize, width: markSize, height: markSize });
    textX = x + markSize + 10;
  }
  // wordmark vertically centered on the mark
  const wordmark = "hari.co.id";
  const wordSize = 14;
  page.drawText(wordmark, {
    x: textX,
    y: topY - markSize / 2 - 1, // slight optical adjustment
    size: wordSize,
    font: bold,
    color: TEXT,
  });
  page.drawText(tagline, {
    x: textX,
    y: topY - markSize + 2,
    size: 8,
    font,
    color: GRAY,
  });
  // bottom of the block (mark is the tallest element)
  return topY - markSize;
}

/** Naive word-wrap. Returns lines that fit within maxWidth at given font size. */
export function wrapText(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const w of words) {
    const candidate = current ? `${current} ${w}` : w;
    if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      // single word too wide — hard break
      if (font.widthOfTextAtSize(w, size) > maxWidth) {
        let chunk = "";
        for (const ch of w) {
          if (font.widthOfTextAtSize(chunk + ch, size) > maxWidth) {
            lines.push(chunk);
            chunk = ch;
          } else chunk += ch;
        }
        current = chunk;
      } else {
        current = w;
      }
    }
  }
  if (current) lines.push(current);
  return lines;
}

export function drawFooter(page: PDFPage, font: PDFFont, sourceUrl: string) {
  const w = page.getWidth();
  // Mini legend
  page.drawText("Legenda:", { x: 30, y: 50, size: 7, font, color: GRAY });
  drawDot(page, 65, 52, RED);
  page.drawText("Libur Nasional", { x: 72, y: 50, size: 7, font, color: GRAY });
  drawDot(page, 142, 52, BLUE);
  page.drawText("Cuti Bersama", { x: 149, y: 50, size: 7, font, color: GRAY });
  drawDot(page, 213, 52, GRAY);
  page.drawText("Hari Penting", { x: 220, y: 50, size: 7, font, color: GRAY });
  drawDot(page, 285, 52, GREEN);
  page.drawText("Hari Besar Islam", { x: 292, y: 50, size: 7, font, color: GRAY });

  page.drawText(DISCLAIMER_SKB, { x: 30, y: 36, size: 7, font, color: GRAY, maxWidth: w - 60 });
  page.drawText(`Sumber: ${sourceUrl} • Dibuat: ${new Date().toLocaleDateString("id-ID")}`, {
    x: 30, y: 18, size: 7, font, color: GRAY,
  });
}

export function drawDot(page: PDFPage, x: number, y: number, color: ReturnType<typeof rgb>) {
  page.drawCircle({ x, y: y, size: 2.5, color });
}

/** Draw a vertical legend list. Returns updated y. */
export function drawLegendList(
  page: PDFPage,
  font: PDFFont,
  bold: PDFFont,
  items: Array<{ label: string; color: ReturnType<typeof rgb> }>,
  x: number,
  yStart: number,
  maxWidth: number,
  title?: string,
): number {
  let y = yStart;
  if (title) {
    page.drawText(title, { x, y, size: 10, font: bold, color: TEXT });
    y -= 14;
  }
  for (const it of items) {
    if (y < 70) break;
    page.drawCircle({ x: x + 3, y: y + 3, size: 2.6, color: it.color });
    const lines = wrapText(it.label, font, 8, maxWidth - 12);
    for (const ln of lines) {
      page.drawText(ln, { x: x + 10, y, size: 8, font, color: TEXT });
      y -= 11;
    }
    y -= 1;
  }
  return y;
}

export const DAY_NAMES_SHORT_ID_PDF = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];

export function downloadPDF(bytes: Uint8Array, filename: string) {
  const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}